import { useEffect, useMemo, useState } from 'react';
import { Navigate, useOutletContext } from 'react-router-dom';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { AbsenceCalendarRow, AbsenceType, EmployeeListItem } from '@ip/shared-types';
import { ABSENCE_TYPES } from '@ip/shared-types';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import type { AppShellContext } from '@/components/layout/AppShell';
import { HrTabs } from '@/components/hr/HrTabs';
import { TeamCalendarGrid } from '@/components/hr/TeamCalendarGrid';
import { ComplianceAdminTable } from '@/components/hr/ComplianceAdminTable';
import { EntitlementTable } from '@/components/hr/EntitlementTable';
import { SickLeavePanel } from '@/components/hr/SickLeavePanel';
import { HrStats } from '@/components/hr/HrStats';
import { PayrollCalculator } from '@/components/hr/PayrollCalculator';
import { useHrMe, useHolidays } from '@/components/hr/useHrData';
import { useHrApi, useEmployeesApi, SessionExpiredError } from '@/services/api';
import { absenceTypeLabel, addMonths, formatMonthYear, monthEnd, monthStart, todayYmd } from '@/lib/hr';
import { t } from '@/i18n';

/**
 * HR / owner surface (Task HR4 surface 3): company calendar with filters,
 * the ewidencja urlopowa (inline-editable), the e-ZLA register + PUE import
 * wizard, and absence statistics with CSV export.
 */
export function HrAdminPage() {
  const { nip } = useOutletContext<AppShellContext>();
  const { hrMe, loading: meLoading } = useHrMe(nip);
  const { absenceCalendar } = useHrApi();
  const { list: listEmployees } = useEmployeesApi();

  const today = todayYmd();
  const [anchor, setAnchor] = useState(() => monthStart(today));
  const [view, setView] = useState<'month' | 'quarter'>('month');
  const [dept, setDept] = useState('all');
  const [typeFilter, setTypeFilter] = useState<'all' | AbsenceType>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'approved'>('all');

  const from = monthStart(anchor);
  const to = view === 'month' ? monthEnd(anchor) : monthEnd(addMonths(anchor, 2));
  const holidays = useHolidays(nip, [Number(from.slice(0, 4)), Number(to.slice(0, 4))]);

  const [rows, setRows] = useState<AbsenceCalendarRow[]>([]);
  const [gridLoading, setGridLoading] = useState(true);
  const [employees, setEmployees] = useState<EmployeeListItem[]>([]);

  const isHrAdmin = !!hrMe?.isHrAdmin;

  useEffect(() => {
    if (!isHrAdmin) return;
    let mounted = true;
    setGridLoading(true);
    absenceCalendar(nip, from, to, 'company')
      .then((r) => { if (mounted) setRows(r); })
      .catch((error) => {
        if (!(error instanceof SessionExpiredError)) toast.error(t('hr.common.loadFailed'));
      })
      .finally(() => { if (mounted) setGridLoading(false); });
    return () => { mounted = false; };
  }, [nip, from, to, isHrAdmin, absenceCalendar]);

  useEffect(() => {
    if (!isHrAdmin) return;
    let mounted = true;
    listEmployees(nip, { pageSize: 200 })
      .then((res) => { if (mounted) setEmployees(res.items); })
      .catch(() => { /* the sick-leave dialog degrades to an empty select */ });
    return () => { mounted = false; };
  }, [nip, isHrAdmin, listEmployees]);

  const departments = useMemo(
    () => [...new Set(rows.map((r) => r.department).filter((d): d is string => !!d))].sort(),
    [rows],
  );

  const filteredRows = useMemo(() => {
    let out = rows;
    if (dept !== 'all') out = out.filter((r) => r.department === dept);
    if (typeFilter !== 'all' || statusFilter !== 'all') {
      out = out.map((r) => ({
        ...r,
        entries: r.entries.filter(
          (e) =>
            (typeFilter === 'all' || e.type === typeFilter) &&
            (statusFilter === 'all' || e.status === 'approved'),
        ),
      }));
    }
    return out;
  }, [rows, dept, typeFilter, statusFilter]);

  const sickEmployees = useMemo(
    () => employees.map((e) => ({ employeeId: e.id, firstName: e.firstName, lastName: e.lastName })),
    [employees],
  );

  if (meLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-9 w-64" />
        <Skeleton className="h-10 w-96" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (hrMe && !isHrAdmin) return <Navigate to={`/${nip}/hr`} replace />;

  return (
    <div className="flex flex-col gap-4">
      <HrTabs nip={nip} hrMe={hrMe} />

      <Tabs defaultValue="calendar">
        <TabsList>
          <TabsTrigger value="calendar">{t('hr.admin.tabs.calendar')}</TabsTrigger>
          <TabsTrigger value="register">{t('hr.admin.tabs.register')}</TabsTrigger>
          <TabsTrigger value="compliance">{t('hr.admin.tabs.compliance')}</TabsTrigger>
          <TabsTrigger value="sick">{t('hr.admin.tabs.sick')}</TabsTrigger>
          <TabsTrigger value="stats">{t('hr.admin.tabs.stats')}</TabsTrigger>
          <TabsTrigger value="payroll">{t('hr.admin.tabs.payroll')}</TabsTrigger>
        </TabsList>

        <TabsContent value="calendar" className="mt-4">
          <Card>
            <CardContent className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-sm font-semibold capitalize">{formatMonthYear(anchor)}</span>
                <div className="flex flex-wrap items-center gap-2">
                  {departments.length > 0 && (
                    <Select value={dept} onValueChange={setDept}>
                      <SelectTrigger className="h-8 w-40" aria-label={t('hr.grid.department')}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('hr.grid.allDepartments')}</SelectItem>
                        {departments.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                  <Select value={typeFilter} onValueChange={(v) => setTypeFilter(v as 'all' | AbsenceType)}>
                    <SelectTrigger className="h-8 w-44" aria-label={t('hr.grid.type')}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('hr.grid.allTypes')}</SelectItem>
                      {ABSENCE_TYPES.map((type) => (
                        <SelectItem key={type} value={type}>{absenceTypeLabel(type)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as 'all' | 'approved')}>
                    <SelectTrigger className="h-8 w-44" aria-label={t('hr.grid.allStatuses')}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('hr.grid.allStatuses')}</SelectItem>
                      <SelectItem value="approved">{t('hr.grid.approvedOnly')}</SelectItem>
                    </SelectContent>
                  </Select>
                  <ToggleGroup
                    type="single"
                    variant="outline"
                    size="sm"
                    value={view}
                    onValueChange={(v) => v && setView(v as 'month' | 'quarter')}
                  >
                    <ToggleGroupItem value="month">{t('hr.grid.month')}</ToggleGroupItem>
                    <ToggleGroupItem value="quarter">{t('hr.grid.quarter')}</ToggleGroupItem>
                  </ToggleGroup>
                  <div className="flex items-center gap-1">
                    <Button variant="outline" size="icon" className="size-8" aria-label={t('hr.calendar.prevMonth')}
                      onClick={() => setAnchor(addMonths(anchor, -1))}>
                      <ChevronLeft className="size-4" />
                    </Button>
                    <Button variant="outline" size="sm" className="h-8" onClick={() => setAnchor(monthStart(today))}>
                      {t('hr.calendar.today')}
                    </Button>
                    <Button variant="outline" size="icon" className="size-8" aria-label={t('hr.calendar.nextMonth')}
                      onClick={() => setAnchor(addMonths(anchor, 1))}>
                      <ChevronRight className="size-4" />
                    </Button>
                  </div>
                </div>
              </div>
              <TeamCalendarGrid
                rows={filteredRows}
                from={from}
                to={to}
                holidays={holidays}
                isHr
                loading={gridLoading}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="register" className="mt-4">
          <Card>
            <CardContent>
              <EntitlementTable nip={nip} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="compliance" className="mt-4">
          <ComplianceAdminTable nip={nip} />
        </TabsContent>

        <TabsContent value="sick" className="mt-4">
          <Card>
            <CardContent>
              <SickLeavePanel nip={nip} employees={sickEmployees} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="stats" className="mt-4">
          <HrStats nip={nip} headcount={rows.length || employees.length} holidays={holidays} />
        </TabsContent>

        <TabsContent value="payroll" className="mt-4">
          <PayrollCalculator nip={nip} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
